import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import { Box, ButtonBase, Stack, Typography } from "@mui/material"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import { SCHEME_STATUS } from "./constants"
import { formatAmount, formatDate } from "./format"
import { getSchemeMetrics } from "./selectors"
import SchemeProgressRing from "./SchemeProgressRing"
import SchemeStatusChip from "./SchemeStatusChip"
import schemeTokens from "./tokens"

const getNote = (scheme, metrics) => {
  if (scheme.status === SCHEME_STATUS.CANCELLED) {
    return `${STRING_CONSTANTS.CANCELLED_ON} ${formatDate(scheme.cancelledAt)}`
  }
  if (scheme.status === SCHEME_STATUS.MATURED) {
    return `${STRING_CONSTANTS.MATURED_ON} ${formatDate(scheme.maturedAt)}`
  }
  if (metrics.isAwaitingActivation) {
    return STRING_CONSTANTS.AWAITING_ACTIVATION_PAYMENT
  }
  if (metrics.isInGrace && metrics.graceEndsOn) {
    return `${STRING_CONSTANTS.PAY_BY} ${formatDate(metrics.graceEndsOn)}`
  }
  if (metrics.nextDueDate) {
    return `${STRING_CONSTANTS.NEXT_DUE} ${formatDate(metrics.nextDueDate)}`
  }
  return metrics.missWarning || STRING_CONSTANTS.ALL_INSTALMENTS_RECORDED
}

const SchemeCard = ({ scheme, onSelect }) => {
  const metrics = getSchemeMetrics(scheme)
  const accumulatedNote = getNote(scheme, metrics)

  return (
    <ButtonBase
      onClick={() => onSelect(scheme.id)}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        borderRadius: schemeTokens.radius.card,
        border: "1px solid",
        borderColor: schemeTokens.color.border,
        bgcolor: schemeTokens.color.surface,
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          <SchemeStatusChip status={scheme.status} />
          <Box
            component="span"
            sx={{
              ...schemeTokens.text.schemeId,
              px: 1,
              py: 0.4,
              borderRadius: schemeTokens.radius.pill,
              bgcolor: schemeTokens.color.trackBg,
              color: schemeTokens.color.muted,
            }}
          >
            {scheme.id}
          </Box>
          {metrics.missCount > 0 && (
            <Box
              component="span"
              sx={{
                ...schemeTokens.text.eyebrow,
                px: 1,
                py: 0.4,
                borderRadius: schemeTokens.radius.pill,
                bgcolor: "error.light",
                color: "error.dark",
              }}
            >
              {`${metrics.missCount} ${STRING_CONSTANTS.MISSED_LABEL}`}
            </Box>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mt: 1.25 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={schemeTokens.text.cardTitle} noWrap>
              {scheme.schemeMaster?.name || STRING_CONSTANTS.SCHEME}
            </Typography>
            <Typography sx={{ ...schemeTokens.text.hint, mt: 0.25 }}>
              {STRING_CONSTANTS.MONTHLY_DEPOSIT}
              {formatAmount(metrics.monthlyAmount)}
            </Typography>
          </Box>
          <ChevronRightRoundedIcon
            sx={{ color: "grey.400", flexShrink: 0 }}
          />
        </Stack>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid",
          borderColor: schemeTokens.color.border,
          bgcolor: schemeTokens.color.stripBg,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <SchemeProgressRing
            value={metrics.paidCount}
            total={metrics.totalInstalments}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
              {formatAmount(metrics.paidTotal)}{" "}
              {STRING_CONSTANTS.ACCUMULATED}
            </Typography>
            <Typography sx={schemeTokens.text.hint} noWrap>
              {accumulatedNote}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography sx={schemeTokens.text.statLabel}>
            {metrics.isProjectedValue
              ? STRING_CONSTANTS.EST_VALUE
              : STRING_CONSTANTS.WALLET_VALUE}
          </Typography>
          <Typography
            sx={{ fontSize: 15, fontWeight: 800, color: "primary.main" }}
          >
            {formatAmount(metrics.walletValue)}
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  )
}

export default SchemeCard
